import React, {
  ReactNode,
  useCallback,
}                           from 'react';
import styled, { css }      from 'styled-components';
import { Button }           from '@material-ui/core';
import { IBannerAd }        from '@typings/nationalAdManagement';
import { EBackdropType }    from '@enums/nationalAdManagement';
import { checkUrlProtocol } from '@utils/commonFunctions';
import OceanSide            from '../assets/oceanside.png';
import MountainSide         from '../assets/mountainside.png';
import MidwestPlains        from '../assets/midwest-plains.png';
import DesertRoad           from '../assets/desert-road.png';
import CityScape            from '../assets/cityscape.png';
import {
  useMeasure,
  TElementSize,
}                           from '../utils/useMeasure';

const BACKDROPS = new Map<EBackdropType, ReactNode>([
  [EBackdropType.Oceanside,     OceanSide],
  [EBackdropType.MountainView,  MountainSide],
  [EBackdropType.MidwestFields, MidwestPlains],
  [EBackdropType.DesertRoad,    DesertRoad],
  [EBackdropType.Cityscape,     CityScape],
]);

interface IStyledProps {
  backdropType? : EBackdropType;
  elSize?       : TElementSize;
}

const BannerWrapper = styled.div<IStyledProps>`
  display             : flex;
  justify-content     : flex-end;
  width               : 100%;
  background-repeat   : no-repeat;
  background-color    : ${({ theme: { backgroundColor } }): string => backgroundColor.darkBlue};
  background-position : right;
  background-size     : inherit;
  ${({ backdropType }) => `background-image: url(${BACKDROPS.get(backdropType ?? EBackdropType.Oceanside)})`};
`;

const BannerContainer = styled.div<IStyledProps>`
  flex            : 1;
  display         : flex;
  flex-direction  : column;
  justify-content : space-evenly;
  align-items     : center;
  padding         : 15px;
  background      : linear-gradient(90deg, rgba(0,99,165,1) 22%, rgba(144,201,239,0.65) 100%);

  ${({ elSize }) => (elSize !== 'xs' && elSize !== 'sm')
  && css`
    padding        : 25px 30px 25px 30px;
    flex-direction : row;
  `};

  > span:first-child {
    width     : 100%;
    max-width : 700px;

    ${({ elSize }) => (elSize === 'md') && 'padding: 0 25px;'};
  }
`;

const TitleText = styled.h1<IStyledProps>`
  font-size  : 16px;
  text-align : center;
  color      : ${({ theme: { textColor } }): string => textColor.white};

  ${({ elSize }) => (elSize !== 'xs') && 'font-size: 20px;'};

  ${({ elSize }) => (elSize !== 'xs' && elSize !== 'sm')
  && css`
    font-size  : 24px;
    text-align : left;
  `};

  ${({ elSize }) => (elSize === 'md') && 'font-size: 30px;'};

  ${({ elSize }) => (elSize === 'lg' || elSize === 'xl') && 'font-size: 36px;'};
`;

const SubText = styled.h3<IStyledProps>`
  font-size   : 14px;
  font-weight : 300;
  text-align  : center;
  color       : ${({ theme: { textColor } }): string => textColor.white};

  ${({ elSize }) => (elSize !== 'xs') && 'font-size: 16px;'};

  ${({ elSize }) => (elSize !== 'xs' && elSize !== 'sm')
  && css`
    font-size  : 18px;
    text-align : right;
  `};
`;

const StyledButton = styled(Button)<IStyledProps>`
  &&{
    white-space  : nowrap;
    margin       : 15px 25px;
    border-color : ${({ theme: { textColor } }): string => textColor.white};

    span {
      font-weight : 500;
      padding     : 0 20px;
      color       : ${({ theme: { textColor } }): string => textColor.white};
    }

  ${({ elSize }) => (elSize !== 'xs' && elSize !== 'sm') && 'margin: 0 0 0 25px;'};
  }
`;

interface IProps {
  bannerData: Partial<IBannerAd>;
}

export const BannerItem: React.FC<IProps> = ({ bannerData }) => {
  const [elSize, elRef] = useMeasure();

  const onLinkClick = useCallback(
    (linkUrl?: string): void => {
      if (!linkUrl) {
        return;
      }

      window.open(checkUrlProtocol(linkUrl));
    },
    [],
  );

  return (
    <BannerWrapper
      ref          = {elRef}
      backdropType = {bannerData?.backdropType}
    >
      <BannerContainer elSize={elSize}>
        <span>
          <TitleText elSize={elSize}>
            {bannerData?.titleText || 'Enter your title text...'}
          </TitleText>
          <SubText elSize={elSize}>
            {bannerData?.subtext || 'Enter your sub text...'}
          </SubText>
        </span>
        {bannerData?.isClickable && (
          <span>
            <StyledButton
              elSize  = {elSize}
              variant = "outlined"
              onClick = {() => onLinkClick(bannerData?.linkUrl)}
            >
              {bannerData?.buttonText || 'Button Text'}
            </StyledButton>
          </span>
        )}
      </BannerContainer>
    </BannerWrapper>
  );
};
